/**
 * InstanceBuffer — VAO + динамический Float32Array для instanced rendering.
 *
 * Инкапсулирует:
 * - Создание VAO с описанием layout атрибутов
 * - Один VBO для instance data
 * - Динамическое увеличение capacity (×2)
 * - Upload данных через bufferSubData / bufferData
 *
 * VAO создаётся один раз и не мутируется (MDN: "avoid changing VAO attachments").
 */

import { IAttributeLayout } from "../types/webgl_types.js";

export class InstanceBuffer
{
  private static allocated_bytes = 0;

  public static getAllocatedBytes(): number
  {
    return this.allocated_bytes;
  }

  private vao             : WebGLVertexArrayObject;
  private vbo             : WebGLBuffer;
  private data            : Float32Array;
  private capacity        : number;
  private capacityChanged : boolean = false;

  /** Количество float-ов на один инстанс */
  public readonly floats_per_instance: number;

  constructor( gl: WebGL2RenderingContext, layout: IAttributeLayout[], initial_capacity: number = 16 )
  {
    this.floats_per_instance = layout.reduce( ( sum, a ) => sum + a.size, 0 );
    this.capacity = initial_capacity;
    this.data = new Float32Array( this.capacity * this.floats_per_instance );
    InstanceBuffer.allocated_bytes += this.data.byteLength;

    // Создаём неизменяемый VAO
    this.vao = gl.createVertexArray()!;
    gl.bindVertexArray( this.vao );

    this.vbo = gl.createBuffer()!;
    gl.bindBuffer( gl.ARRAY_BUFFER, this.vbo );
    gl.bufferData( gl.ARRAY_BUFFER, this.data, gl.DYNAMIC_DRAW );

    const stride = this.floats_per_instance * Float32Array.BYTES_PER_ELEMENT;
    let offset  = 0;

    for ( const attr of layout )
    {
      gl.enableVertexAttribArray( attr.location );
      gl.vertexAttribPointer( attr.location, attr.size, gl.FLOAT, false, stride, offset );
      gl.vertexAttribDivisor( attr.location, 1 ); // instanced
      offset += attr.size * Float32Array.BYTES_PER_ELEMENT;
    }

    gl.bindVertexArray( null );
    gl.bindBuffer( gl.ARRAY_BUFFER, null );
  }

  /** Увеличивает capacity × 2, пока count не поместится */
  public ensureCapacity( count: number ): void
  {
    if ( count <= this.capacity )
      return;

    while ( this.capacity < count )
      this.capacity *= 2;

    InstanceBuffer.allocated_bytes -= this.data.byteLength;
    this.data = new Float32Array( this.capacity * this.floats_per_instance );
    InstanceBuffer.allocated_bytes += this.data.byteLength;
    this.capacityChanged = true;
  }

  /** Возвращает Float32Array для прямой записи данных */
  public getDataArray(): Float32Array
  {
    return this.data;
  }

  /** Загружает active_count инстансов на GPU */
  public upload( gl: WebGL2RenderingContext, active_count: number ): void
  {
    gl.bindBuffer( gl.ARRAY_BUFFER, this.vbo );

    if ( this.capacityChanged )
    {
      gl.bufferData( gl.ARRAY_BUFFER, this.data, gl.DYNAMIC_DRAW );
      this.capacityChanged = false;
    }

    gl.bufferSubData(
      gl.ARRAY_BUFFER,
      0,
      this.data.subarray( 0, active_count * this.floats_per_instance ),
    );
  }

  public bind( gl: WebGL2RenderingContext ): void
  {
    gl.bindVertexArray( this.vao );
  }

  public unbind( gl: WebGL2RenderingContext ): void
  {
    gl.bindVertexArray( null );
  }

  public dispose( gl: WebGL2RenderingContext ): void
  {
    InstanceBuffer.allocated_bytes -= this.data.byteLength;
    gl.deleteBuffer( this.vbo );
    gl.deleteVertexArray( this.vao );
  }
}
